import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Layout from '../../../../Components/layouts';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import AppLoader from '../../../../Components/AppLoader';
import { RouteSkeleton } from '../../../../Components/RouteTransition';
import { interactiveCardStyles, pageEyebrowStyles, pageTitleTextStyles, surfaceCardStyles } from '../../../../theme/commonStyles';
import MenuList from '@material-ui/core/MenuList';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { SkillsAndTransformation } from 'Services/SkillsAndTransformation';
import {
  isLearningHubAccessClosed,
  isLearningHubUnavailableResponse,
  setLearningHubAccess,
} from 'utils/learningHubAvailability';
import { UserPath } from 'Routes/routePaths';
import { DEFAULT_COURSE_IMAGE_URL, shouldOpenCourseInNewTab } from 'utils/constants';
import { useImpersonation } from 'hooks/useImpersonation';
import {
  courseLaunchLabel,
  courseLaunchUrl,
  OPEN_COURSE_LINK_NOTE,
} from 'utils/courseLaunch';
import { useMoodleCourseIframeGuard } from 'hooks/useMoodleCourseIframeGuard';
import { useCourseIframeSelection } from 'hooks/useCourseIframeSelection';
import { notify } from 'utils';
import CourseIframeToolbar from '../../../../Components/CourseIframeToolbar';
import LearningHubAccessStrip from '../../../../Components/LearningHubAccessStrip';
import CourseDetailsDrawer from './CourseDetailsDrawer';
import UpgradePackageDialog from './UpgradePackageDialog';
import { CategoryCount, categoryNavStyles } from '../categoryNavStyles';
import {
  accessCtaLabel,
  buildLearningHubQuery,
  catalogueFilterOptions,
  mergeCertificateIssuers,
  cardAccent,
  chipSurface,
  courseCardFields,
  levelTone,
  DEFAULT_LEARNING_HUB_PAGE_SIZE,
  emptyHubPagination,
  HUB_TAB_FREEMIUM,
  HUB_TAB_LEARNING_HUB,
  LEARNING_HUB_PAGE_SIZES,
  learningHubSubtitle,
  normalizeCategories,
  normalizePageSize,
  parseHubTab,
  planLabel,
  hubGrantedFromResponse,
  resolveAccessCta,
  resolveHubPagination,
  resolvePagedCourses,
  resolveTabPayload,
  resultLineForTab,
} from './helpers';

const skillsAndTransformation = new SkillsAndTransformation();
const EMPTY_CATALOGUE_FILTERS = {
  levels: [],
  audiences: [],
  durations: [],
  certificateIssuers: [],
};

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    marginBottom: theme.spacing(2.5),
  },
  pageEyebrow: {
    ...pageEyebrowStyles,
    marginBottom: theme.spacing(0.5),
  },
  pageTitle: {
    ...pageTitleTextStyles,
    marginBottom: 0,
  },
  pageTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  titleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.5),
  },
  planChip: {
    fontWeight: 700,
    borderRadius: 999,
  },
  headerUpgradeBtn: {
    borderRadius: '999px !important',
    minHeight: 36,
    padding: '0 16px',
    textTransform: 'none',
    fontWeight: 650,
    letterSpacing: '0.01em',
    boxShadow: 'none !important',
    '&.MuiButton-outlined': {
      borderColor: 'transparent !important',
      backgroundColor: 'rgba(47, 111, 222, 0.1) !important',
      color: '#2f6fde !important',
      '&:hover': {
        borderColor: 'transparent !important',
        backgroundColor: 'rgba(47, 111, 222, 0.16) !important',
      },
    },
    '&.MuiButton-contained': {
      backgroundColor: '#2f6fde !important',
      color: '#fff !important',
      '&:hover': {
        backgroundColor: '#275fc2 !important',
      },
    },
  },
  pageDescription: {
    color: '#647184',
    fontSize: '0.875rem',
    lineHeight: 1.55,
    maxWidth: 760,
  },
  tabs: {
    marginTop: theme.spacing(1),
    minHeight: 40,
    borderBottom: '1px solid #e7ebf0',
    '& .MuiTab-root': {
      minHeight: 40,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.25),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  searchField: {
    minWidth: 220,
    flex: '1 1 220px',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#fff',
    },
  },
  filterControl: {
    minWidth: 168,
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#fff',
    },
  },
  issuerControl: {
    minWidth: 220,
    width: 220,
    flex: '0 0 220px',
  },
  clearFiltersBtn: {
    minHeight: 40,
    borderRadius: 8,
    borderColor: '#e7ebf0',
    color: '#647184',
    fontSize: '0.8125rem',
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: '#d5dde8',
      backgroundColor: '#f5f8fc',
      color: '#263548',
    },
  },
  levelChip: {
    borderRadius: 8,
    fontWeight: 600,
  },
  resultLine: {
    color: '#647184',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    marginBottom: theme.spacing(1),
  },
  paginationRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  pageSizeControl: {
    minWidth: 130,
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#fff',
    },
  },
  gridLoading: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: theme.spacing(6, 0),
  },
  freemiumCallout: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: '1px solid #e7ebf0',
    backgroundColor: '#f5f8fc',
  },
  freemiumCalloutText: {
    color: '#263548',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    flex: '1 1 280px',
  },
  freemiumCalloutActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  layoutGrid: {
    alignItems: 'stretch',
  },
  ...categoryNavStyles(theme),
  content: {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'visible',
    boxSizing: 'border-box',
    padding: theme.spacing(2, 2.25),
    backgroundImage: 'linear-gradient(165deg, var(--card-wash, transparent) 0%, transparent 62%)',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  title: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#263548',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    lineHeight: 1.35,
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.0625rem',
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  newBadge: {
    flexShrink: 0,
    marginTop: 2,
    backgroundColor: '#EC7A12',
    color: '#fff',
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderRadius: 4,
    padding: '2px 6px',
    lineHeight: 1.4,
  },
  chipRow: {
    marginTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  issuerChip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    padding: '4px 10px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1.4,
    width: 'fit-content',
  },
  levelOutline: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: 'transparent',
    color: '#647184',
    border: '1px solid #d5dde8',
    padding: '3px 10px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1.4,
    width: 'fit-content',
  },
  levelFoundational: {
    color: '#0c7a56',
    backgroundColor: 'rgba(15, 155, 110, 0.14)',
    borderColor: 'rgba(12, 122, 86, 0.35)',
  },
  levelIntermediate: {
    color: '#9a5410',
    backgroundColor: 'rgba(224, 122, 47, 0.16)',
    borderColor: 'rgba(184, 98, 16, 0.38)',
  },
  levelAdvanced: {
    color: '#6d3bb8',
    backgroundColor: 'rgba(124, 92, 191, 0.16)',
    borderColor: 'rgba(109, 59, 184, 0.38)',
  },
  factRow: {
    marginTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    color: '#647184',
    fontSize: 13,
    fontWeight: 500,
  },
  fact: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  factIcon: {
    fontSize: 16,
    color: 'var(--card-accent, #3d7ddd)',
  },
  desc: {
    margin: '8px 0 0',
    color: '#647184',
    fontSize: '0.875rem',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  descBox: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  mainContent: {
    minWidth: 0,
  },
  courseActionBtn: {
    borderRadius: '999px !important',
    minHeight: 36,
    padding: '0 16px',
    color: '#fff !important',
    fontSize: 13,
    fontWeight: 650,
    letterSpacing: '0.01em',
    minWidth: 0,
    textTransform: 'none',
    boxShadow: 'none !important',
    transition: 'filter 150ms ease, transform 150ms ease',
    '&:hover': {
      boxShadow: 'none !important',
      filter: 'brightness(0.94)',
    },
  },
  detailsBtn: {
    borderRadius: '999px !important',
    minHeight: 36,
    padding: '0 16px',
    fontSize: 13,
    fontWeight: 650,
    letterSpacing: '0.01em',
    textTransform: 'none',
    border: '1px solid transparent !important',
    backgroundColor: 'color-mix(in srgb, var(--card-accent, #2f6fde) 14%, transparent) !important',
    color: 'var(--card-accent, #2f6fde) !important',
    '&:hover': {
      borderColor: 'transparent !important',
      backgroundColor: 'color-mix(in srgb, var(--card-accent, #2f6fde) 22%, transparent) !important',
    },
  },
  enrolBtn: {
    backgroundColor: 'var(--card-accent, #2f6fde) !important',
    '&:hover': {
      backgroundColor: 'var(--card-accent, #2f6fde) !important',
    },
  },
  launchBtn: {
    backgroundColor: '#12924a !important',
    '&:hover': {
      backgroundColor: '#12924a !important',
    },
  },
  upgradeBtn: {
    backgroundColor: 'var(--card-accent, #2f6fde) !important',
    '&:hover': {
      backgroundColor: 'var(--card-accent, #2f6fde) !important',
    },
  },
  requestBtn: {
    backgroundColor: '#e07a2f !important',
    '&:hover': {
      backgroundColor: '#e07a2f !important',
    },
  },
  cardRoot: {
    ...interactiveCardStyles,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  thumbnailWrap: {
    position: 'relative',
    width: '100%',
    backgroundColor: '#f5f8fc',
    borderBottom: '1px solid #e7ebf0',
    overflow: 'hidden',
    lineHeight: 0,
  },
  thumbnailPending: {
    minHeight: 180,
  },
  categoryOverlay: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 1,
    maxWidth: 'calc(100% - 24px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    backgroundColor: 'rgba(38, 53, 72, 0.78)',
    color: '#fff',
    borderRadius: 8,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  thumbnail: {
    display: 'block',
    width: '100%',
    height: 'auto',
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },
  thumbnailLoaded: {
    opacity: 1,
  },
  cardActionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 16,
    paddingTop: 12,
    gap: 8,
    minWidth: 0,
    flexShrink: 0,
    flexWrap: 'wrap',
    borderTop: '1px solid rgba(148, 163, 184, 0.28)',
  },
  coursesGrid: {
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 0),
    },
    alignItems: 'stretch',
  },
  emptyState: {
    color: '#647184',
    fontSize: '0.9375rem',
    fontWeight: 500,
    textAlign: 'center',
    padding: theme.spacing(4, 2),
    width: '100%',
  },
  requestsPanel: {
    ...surfaceCardStyles,
    padding: theme.spacing(2.5),
  },
  requestRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderBottom: '1px solid #e7ebf0',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  requestTitle: {
    fontWeight: 600,
    color: '#263548',
    fontSize: '0.9375rem',
  },
  requestMeta: {
    color: '#647184',
    fontSize: '0.8125rem',
    marginTop: 4,
  },
  fullWidthCourseWrap: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  iframeContainer: {
    ...surfaceCardStyles,
    position: 'relative',
    width: '100%',
    height: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  },
  iframeContainerMaximized: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1400,
    width: '100%',
    height: '100%',
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  courseIframe: {
    border: 'none',
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#fff',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  },
}));

const courseImageUrl = (course) =>
  courseCardFields(course).image.trim() || DEFAULT_COURSE_IMAGE_URL;

const courseBlurb = (course) =>
  String(course?.summary || course?.description || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const LazyThumbnail = ({
  src,
  alt,
  wrapClassName,
  pendingClassName,
  imageClassName,
  loadedClassName,
  overlay,
}) => {
  const placeholderRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(src || DEFAULT_COURSE_IMAGE_URL);

  useEffect(() => {
    setImageSrc(src || DEFAULT_COURSE_IMAGE_URL);
    setIsLoaded(false);
  }, [src]);

  useEffect(() => {
    const node = placeholderRef.current;
    if (!node || isVisible) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '240px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <div className={`${wrapClassName}${isLoaded ? '' : ` ${pendingClassName || ''}`}`} ref={placeholderRef}>
      {overlay}
      {isVisible ? (
        <img
          loading="lazy"
          className={`${imageClassName}${isLoaded ? ` ${loadedClassName}` : ''}`}
          src={imageSrc}
          alt={alt}
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            if (imageSrc !== DEFAULT_COURSE_IMAGE_URL) {
              setImageSrc(DEFAULT_COURSE_IMAGE_URL);
              return;
            }
            setIsLoaded(true);
          }}
        />
      ) : null}
    </div>
  );
};

const ctaButtonClass = (classes, cta) => {
  switch (cta) {
    case 'launch':
      return classes.launchBtn;
    case 'upgrade':
      return classes.upgradeBtn;
    case 'request':
      return classes.requestBtn;
    default:
      return classes.enrolBtn;
  }
};

const CourseItem = ({
  course,
  cta,
  onAction,
  onDetails,
  actionLoadingUuid,
  impersonating,
}) => {
  const classes = useStyles();
  const isLoading = actionLoadingUuid === course.uuid;
  const card = courseCardFields(course);
  const thumbnailUrl = courseImageUrl(course);
  const blurb = courseBlurb(course);
  const actionLabel = accessCtaLabel(cta, impersonating);
  const cornerLabel = card.subcategory || card.category;
  const accent = cardAccent(cornerLabel);
  const levelClass = {
    foundational: classes.levelFoundational,
    intermediate: classes.levelIntermediate,
    advanced: classes.levelAdvanced,
  }[levelTone(card.level)];

  return (
    <Grid item xs={12} sm={6} md={6} lg={6}>
      <Card
        className={classes.cardRoot}
        style={{ '--card-accent': accent, '--card-wash': `${accent}29` }}
      >
        <LazyThumbnail
          src={thumbnailUrl}
          alt={course.name}
          wrapClassName={classes.thumbnailWrap}
          pendingClassName={classes.thumbnailPending}
          imageClassName={classes.thumbnail}
          loadedClassName={classes.thumbnailLoaded}
          overlay={cornerLabel ? (
            <span className={classes.categoryOverlay} style={{ backgroundColor: chipSurface(accent) }}>
              {cornerLabel}
            </span>
          ) : null}
        />
        <CardContent className={classes.content}>
          <div className={classes.descBox}>
            <div className={classes.titleRow}>
              <Typography className={classes.title}>{card.name}</Typography>
              {card.isNew ? <span className={classes.newBadge}>New</span> : null}
            </div>
            <div className={classes.chipRow}>
              {card.certificateIssuers.map((issuer) => (
                <Typography key={issuer} variant="caption" className={classes.issuerChip}>
                  {issuer}
                </Typography>
              ))}
              {card.level ? (
                <Typography
                  variant="caption"
                  className={`${classes.levelOutline}${levelClass ? ` ${levelClass}` : ''}`}
                >
                  {card.level}
                </Typography>
              ) : null}
            </div>
            {card.duration || card.audience ? (
              <div className={classes.factRow}>
                {card.duration ? (
                  <span className={classes.fact}>
                    <AccessTimeIcon className={classes.factIcon} />
                    {card.duration}
                  </span>
                ) : null}
                {card.audience ? (
                  <span className={classes.fact}>
                    <PersonOutlineIcon className={classes.factIcon} />
                    {card.audience}
                  </span>
                ) : null}
              </div>
            ) : null}
            {blurb ? (
              <Typography variant="subtitle2" className={classes.desc} title={blurb}>
                {blurb}
              </Typography>
            ) : null}
          </div>
          <div className={classes.cardActionsRow}>
            <Button
              className={classes.detailsBtn}
              variant="outlined"
              color="primary"
              onClick={() => onDetails(course)}
            >
              Details
            </Button>
            <Button
              className={`${classes.courseActionBtn} ${ctaButtonClass(classes, cta)}`}
              onClick={() => onAction(course)}
              variant="contained"
              disableElevation
              disabled={isLoading}
            >
              {isLoading ? 'Please wait...' : actionLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Grid>
  );
};

const hubTabLabel = (meta, fallback) => {
  const label = meta?.label || fallback;
  if (meta?.count == null) {
    return label;
  }
  return `${label} (${meta.count})`;
};

const LearningHub = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const impersonation = useImpersonation();
  const [ready, setReady] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [tabPending, setTabPending] = useState(false);
  const countsTabRef = useRef(null);
  const [navCategories, setNavCategories] = useState([]);
  const [pageCourses, setPageCourses] = useState([]);
  const [pagination, setPagination] = useState(() => emptyHubPagination());
  const [page, setPage] = useState(0);
  const [perpage, setPerpage] = useState(DEFAULT_LEARNING_HUB_PAGE_SIZE);
  const [access, setAccess] = useState(null);
  const [filters, setFilters] = useState(EMPTY_CATALOGUE_FILTERS);
  const [totals, setTotals] = useState(null);
  const [hubGranted, setHubGranted] = useState(false);
  const [activeCatalogue, setActiveCatalogue] = useState('');
  const [activeSourceCategory, setActiveSourceCategory] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('category') || '';
    } catch (error) {
      return '';
    }
  });
  const [actionLoadingUuid, setActionLoadingUuid] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [level, setLevel] = useState('');
  const [duration, setDuration] = useState('');
  const [audience, setAudience] = useState('');
  const [issuer, setIssuer] = useState('');
  const [hubTab, setHubTab] = useState(() => {
    try {
      return parseHubTab(new URLSearchParams(window.location.search).get('tab'));
    } catch (error) {
      return HUB_TAB_FREEMIUM;
    }
  });
  const [tabMeta, setTabMeta] = useState({
    freemium: { label: 'Freemium' },
    learning_hub: { label: 'Learning Hub' },
  });
  const [detailsCourse, setDetailsCourse] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeBlocked, setUpgradeBlocked] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [isIframeVisible, setIsIframeVisible] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const iframeWrapRef = useRef(null);
  const { iframeRef: courseIframeRef, handleIframeLoad } = useCourseIframeSelection(
    isIframeVisible,
    iframeUrl
  );

  const openCourseInline = (url) => {
    const launchButton = document.activeElement;
    if (launchButton && launchButton !== document.body && typeof launchButton.blur === 'function') {
      launchButton.blur();
    }
    setIframeUrl(url);
    setIframeLoading(true);
    setIsIframeVisible(true);
    setIsMaximized(false);
  };

  const openCourse = (url, provider) => {
    const launchUrl = courseLaunchUrl(url);
    if (shouldOpenCourseInNewTab(provider)) {
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    openCourseInline(launchUrl);
  };

  const closeCourseInline = () => {
    setIsIframeVisible(false);
    setIframeUrl('');
    setIsMaximized(false);
  };

  useMoodleCourseIframeGuard(closeCourseInline);

  useEffect(() => {
    setIsIframeVisible(false);
    setIframeUrl('');
    setIsMaximized(false);
  }, [location.pathname, location.key]);

  const debouncedSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      if (debouncedSearchRef.current === next) {
        return;
      }
      debouncedSearchRef.current = next;
      setDebouncedSearch(next);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = parseHubTab(params.get('tab'));
    const categoryFromUrl = params.get('category') || '';
    if (tabFromUrl !== hubTab) {
      setTabPending(true);
      setNavCategories([]);
      setHubTab(tabFromUrl);
      setPage(0);
    }
    if (categoryFromUrl !== categoryFilter) {
      setCategoryFilter(categoryFromUrl);
      setPage(0);
    }
  }, [location.search, hubTab, categoryFilter]);

  useEffect(() => {
    if (!navCategories.length) {
      return;
    }
    if (!categoryFilter) {
      setActiveCatalogue('');
      setActiveSourceCategory('');
      return;
    }
    const parentMatch = navCategories.find((item) => item.category === categoryFilter);
    if (parentMatch) {
      setActiveCatalogue(categoryFilter);
      setActiveSourceCategory('');
      return;
    }
    const subMatch = navCategories.find((item) =>
      (item.subcategories || []).some((sub) =>
        sub.category === categoryFilter || sub.source_category === categoryFilter
      )
    );
    if (subMatch) {
      setActiveCatalogue(subMatch.category);
      setActiveSourceCategory(categoryFilter);
    }
  }, [navCategories, categoryFilter]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [hubTab, activeCatalogue, activeSourceCategory, debouncedSearch, level, duration, audience, issuer, categoryFilter]);

  useEffect(() => {
    let mounted = true;

    const fetchLearningHub = async () => {
      const switchingTab = countsTabRef.current !== hubTab;
      if (switchingTab) {
        setTabPending(true);
        setNavCategories([]);
      }
      setListLoading(true);

      try {
        const response = await skillsAndTransformation.getLearningHub(
          buildLearningHubQuery({
            tab: hubTab,
            search: debouncedSearch,
            category: categoryFilter,
            level,
            duration,
            audience,
            issuer,
            pageno: page,
            perpage,
          })
        );

        if (!mounted) {
          return;
        }

        const tabPayload = resolveTabPayload(response, hubTab);
        const available = !!(
          response?.status
          && (Array.isArray(response?.courses) || Array.isArray(response?.data))
        );
        if (response?.access) {
          setLearningHubAccess(response.access);
        }

        if (available) {
          const nextFilters = tabPayload.filters || {};
          const categoryTree = Array.isArray(nextFilters.category_tree)
            ? nextFilters.category_tree
            : [];
          const normalizedNav = normalizeCategories(
            categoryTree.length ? categoryTree : tabPayload.data,
            categoryTree
          );
          const nextPagination = resolveHubPagination(tabPayload.pagination, {
            pageno: page,
            perpage,
            courses: resolvePagedCourses(tabPayload),
          });

          setPageCourses(resolvePagedCourses(tabPayload));
          setPagination(nextPagination);
          setNavCategories(normalizedNav);
          if (nextPagination.pageno !== page) {
            setPage(nextPagination.pageno);
          }
          setAccess(response.access || null);
          const nextFilterOptions = catalogueFilterOptions(nextFilters);
          setFilters((current) => ({
            ...nextFilterOptions,
            certificateIssuers: mergeCertificateIssuers(
              current.certificateIssuers,
              nextFilterOptions.certificateIssuers,
              !!issuer
            ),
          }));
          setTotals(tabPayload.totals || null);
          setHubGranted(hubGrantedFromResponse(response));
          setTabMeta((current) => ({
            freemium: {
              label: response.tabs?.freemium?.label || current.freemium.label || 'Freemium',
              count: Number.isFinite(Number(response.tabs?.freemium?.count))
                ? Number(response.tabs.freemium.count)
                : current.freemium.count,
            },
            learning_hub: {
              label: response.tabs?.learning_hub?.label || current.learning_hub.label || 'Learning Hub',
              count: Number.isFinite(Number(response.tabs?.learning_hub?.count))
                ? Number(response.tabs.learning_hub.count)
                : current.learning_hub.count,
            },
          }));
          setActiveCatalogue((current) => (
            current && normalizedNav.some((item) => item.category === current) ? current : ''
          ));
        } else if (isLearningHubAccessClosed(response?.access)) {
          setPageCourses([]);
          setPagination(emptyHubPagination(perpage));
          setNavCategories([]);
          setAccess(response.access);
          setFilters(EMPTY_CATALOGUE_FILTERS);
          setTotals(null);
          setHubGranted(false);
          setActiveCatalogue('');
          setActiveSourceCategory('');
        } else {
          setPageCourses([]);
          setPagination(emptyHubPagination(perpage));
          setNavCategories([]);
          setAccess(null);
          setFilters(EMPTY_CATALOGUE_FILTERS);
          setTotals(null);
          setHubGranted(false);
          setActiveCatalogue('');
          setActiveSourceCategory('');
          if (isLearningHubUnavailableResponse(response)) {
            history.replace(UserPath.RegisteredCourses);
            return;
          }
          notify('error', response?.message || 'Unable to load Learning Hub courses');
        }
      } catch (error) {
        if (!mounted) {
          return;
        }

        setPageCourses([]);
        setPagination(emptyHubPagination(perpage));
        setNavCategories([]);
        setAccess(null);
        setFilters(EMPTY_CATALOGUE_FILTERS);
        setTotals(null);
        setHubGranted(false);
        setActiveCatalogue('');
        setActiveSourceCategory('');
        notify('error', error?.message || 'Unable to load Learning Hub courses');
      } finally {
        if (mounted) {
          countsTabRef.current = hubTab;
          setTabPending(false);
          setListLoading(false);
          setReady(true);
        }
      }
    };

    fetchLearningHub();

    return () => {
      mounted = false;
    };
  }, [
    history,
    hubTab,
    debouncedSearch,
    categoryFilter,
    level,
    duration,
    audience,
    issuer,
    page,
    perpage,
  ]);

  const visibleCourses = pageCourses;

  const refreshCourseEnrollment = (uuid, enrolled, accessCta) => {
    setPageCourses((previousCourses) =>
      previousCourses.map((course) =>
        course.uuid === uuid
          ? {
              ...course,
              enrolled,
              access_cta: accessCta || (enrolled ? 'launch' : course.access_cta),
            }
          : course
      )
    );
    setDetailsCourse((current) => (
      current?.uuid === uuid
        ? {
            ...current,
            enrolled,
            access_cta: accessCta || (enrolled ? 'launch' : current.access_cta),
          }
        : current
    ));
  };

  const applyClosedAccess = (response) => {
    if (!isLearningHubAccessClosed(response?.access)) {
      return;
    }
    setLearningHubAccess(response.access);
    setAccess(response.access);
    setPageCourses([]);
    setPagination(emptyHubPagination(perpage));
    setNavCategories([]);
    setActiveCatalogue('');
    setActiveSourceCategory('');
  };

  const syncQueryToUrl = ({ tab = hubTab, category = categoryFilter } = {}) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', parseHubTab(tab));
    if (category) {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    const search = params.toString();
    setHubTab(parseHubTab(tab));
    setCategoryFilter(category || '');
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  const handleSelectHubTab = (_event, value) => {
    const nextTab = value === 1 ? HUB_TAB_LEARNING_HUB : HUB_TAB_FREEMIUM;
    if (nextTab === hubTab) {
      return;
    }
    setTabPending(true);
    setNavCategories([]);
    setActiveCatalogue('');
    setActiveSourceCategory('');
    setLevel('');
    setDuration('');
    setAudience('');
    setIssuer('');
    setSearchInput('');
    setDebouncedSearch('');
    setPage(0);
    setDetailsCourse(null);
    syncQueryToUrl({ tab: nextTab, category: '' });
  };

  const handleSelectAllCourses = () => {
    setActiveCatalogue('');
    setActiveSourceCategory('');
    setPage(0);
    syncQueryToUrl({ category: '' });
  };

  const handleSelectParentCategory = (category) => {
    setActiveCatalogue(category);
    setActiveSourceCategory('');
    setPage(0);
    syncQueryToUrl({ category });
  };

  const handleSelectSourceCategory = (parentCategory, sourceCategory) => {
    setActiveCatalogue(parentCategory);
    setActiveSourceCategory(sourceCategory);
    setPage(0);
    syncQueryToUrl({ category: sourceCategory });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    debouncedSearchRef.current = '';
    setDebouncedSearch('');
    setLevel('');
    setDuration('');
    setAudience('');
    setIssuer('');
    setPage(0);
    setActiveCatalogue('');
    setActiveSourceCategory('');
    if (categoryFilter) {
      syncQueryToUrl({ category: '' });
    }
  };

  const launchCourse = async (course) => {
    const response = await skillsAndTransformation.launchLearningHubCourse(course.uuid);
    const gotoUrl = response?.data?.goto_url;

    if (gotoUrl) {
      openCourse(gotoUrl, course.provider);
      return true;
    }

    applyClosedAccess(response);
    notify('error', response?.message || 'Launch URL is not available');
    return false;
  };

  const handleCourseAction = async (course) => {
    if (!course?.uuid || actionLoadingUuid) {
      return;
    }

    const cta = resolveAccessCta(course, { tab: hubTab, hubGranted });

    try {
      setActionLoadingUuid(course.uuid);

      if (cta === 'upgrade' || cta === 'request') {
        setUpgradeOpen(true);
        return;
      }

      if (cta === 'launch' || course.enrolled) {
        await launchCourse(course);
        return;
      }

      const response = await skillsAndTransformation.enrollLearningHubCourse(course.uuid);
      if (response?.status) {
        refreshCourseEnrollment(course.uuid, true, 'launch');
        notify('success', response?.message || 'Enrolled successfully');
        await launchCourse(course);
      } else {
        applyClosedAccess(response);
        notify('error', response?.message || 'Unable to enrol in this course');
      }
    } catch (error) {
      notify('error', error?.message || 'Something went wrong');
    } finally {
      setActionLoadingUuid(null);
    }
  };

  const pageTitle = 'Learning Hub';
  const subtitle = impersonation.active
    ? OPEN_COURSE_LINK_NOTE
    : learningHubSubtitle(hubTab, hubGranted);
  const showingCount = visibleCourses.length;
  const resultLine = resultLineForTab(hubTab, showingCount, totals, hubGranted, pagination);
  const tabIndex = hubTab === HUB_TAB_LEARNING_HUB ? 1 : 0;
  const planName = planLabel(access?.plan);
  const showUpgrade = access?.plan === 'freemium' && !upgradeBlocked && !hubGranted;
  const allCoursesActive = !categoryFilter && !activeCatalogue && !activeSourceCategory;
  const hasActiveFilters = Boolean(
    searchInput.trim()
    || debouncedSearch
    || level
    || duration
    || audience
    || issuer
    || categoryFilter
  );
  const navTotal = navCategories.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  const allCourseCount = navTotal || pagination.data_count || 0;

  const detailsCta = detailsCourse
    ? resolveAccessCta(detailsCourse, { tab: hubTab, hubGranted })
    : 'enroll';

  return (
    <Layout lockPageScroll={isIframeVisible}>
      {!ready || tabPending ? (
        <RouteSkeleton variant="learning-hub" />
      ) : isIframeVisible ? (
        <div className={classes.fullWidthCourseWrap}>
          <div
            className={`${classes.iframeContainer}${isMaximized ? ` ${classes.iframeContainerMaximized}` : ''}`}
            ref={iframeWrapRef}
          >
            <CourseIframeToolbar
              isMaximized={isMaximized}
              onToggleMaximize={() => setIsMaximized((prev) => !prev)}
              onClose={closeCourseInline}
            />
            {iframeLoading && <AppLoader overlay />}
            <iframe
              ref={courseIframeRef}
              src={iframeUrl}
              frameBorder="0"
              title={impersonation.active ? 'Course link' : 'Course'}
              className={classes.courseIframe}
              allow="fullscreen; autoplay; picture-in-picture"
              allowFullScreen
              onLoad={() => {
                setIframeLoading(false);
                handleIframeLoad();
              }}
            ></iframe>
          </div>
        </div>
      ) : (
        <>
          <div className={classes.pageHeader}>
            <div className={classes.pageTitleRow}>
              <div>
                <Typography className={classes.pageEyebrow}>Learning</Typography>
                <Typography className={classes.pageTitle}>{pageTitle}</Typography>
              </div>
              <div className={classes.titleActions}>
                {planName ? (
                  <Chip className={classes.planChip} label={planName} color="primary" variant="outlined" />
                ) : null}
                {showUpgrade ? (
                  <Button
                    className={classes.headerUpgradeBtn}
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={() => setUpgradeOpen(true)}
                  >
                    Upgrade package
                  </Button>
                ) : null}
              </div>
            </div>
            <Typography className={classes.pageDescription}>{subtitle}</Typography>
            <LearningHubAccessStrip access={access} />
            <Tabs
              className={classes.tabs}
              value={tabIndex}
              onChange={handleSelectHubTab}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label={hubTabLabel(tabMeta.freemium, 'Freemium')} />
              <Tab label={hubTabLabel(tabMeta.learning_hub, 'Learning Hub')} />
            </Tabs>
          </div>

          {!isLearningHubAccessClosed(access) && (
            <Grid container spacing={2} className={classes.layoutGrid}>
              <Grid item xs={12} md={3} className={classes.sidebarMenu}>
                <Typography className={classes.sidebarTitle} component="h2">Course Category</Typography>
                <MenuList id="learning-hub-categories" open className={classes.categoryList}>
                  <MenuItem
                    onClick={handleSelectAllCourses}
                    className={`${classes.categoryItem}${allCoursesActive ? ` ${classes.categoryItemActive}` : ''}`}
                  >
                    <span className={classes.categoryLabel}>All courses</span>
                    <CategoryCount count={allCourseCount} active={allCoursesActive} classes={classes} />
                  </MenuItem>
                  {navCategories.map((item) => {
                    const parentActive = item.category === activeCatalogue && !activeSourceCategory;
                    const parentCount = item.count != null ? item.count : (item.courses ? item.courses.length : 0);
                    return (
                      <React.Fragment key={item.category}>
                        <MenuItem
                          onClick={() => handleSelectParentCategory(item.category)}
                          className={`${classes.categoryItem}${parentActive ? ` ${classes.categoryItemActive}` : ''}`}
                          title={item.category}
                        >
                          <span className={classes.categoryLabel}>{item.category}</span>
                          <CategoryCount count={parentCount} active={parentActive} classes={classes} />
                        </MenuItem>
                        {(item.subcategories || []).map((sub) => {
                          const subName = sub.category || sub.source_category;
                          const subActive = activeSourceCategory === subName
                            && activeCatalogue === item.category;
                          const subCount = sub.count != null ? sub.count : (sub.courses ? sub.courses.length : 0);
                          return (
                            <MenuItem
                              key={`${item.category}:${subName}`}
                              onClick={() => handleSelectSourceCategory(item.category, subName)}
                              className={`${classes.categoryItem} ${classes.subcategoryItem}${subActive ? ` ${classes.categoryItemActive}` : ''}`}
                              title={subName}
                            >
                              <span className={classes.categoryLabel}>{subName}</span>
                              <CategoryCount count={subCount} active={subActive} classes={classes} />
                            </MenuItem>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </MenuList>
              </Grid>
              <Grid item xs={12} md={9} className={classes.mainContent}>
                <div className={classes.toolbar}>
                  <TextField
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    placeholder="Search courses"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {(filters.levels || []).map((value) => {
                    const selected = level === value;
                    return (
                      <Chip
                        key={value}
                        className={classes.levelChip}
                        label={value}
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'default' : 'outlined'}
                        onClick={() => {
                          setLevel(selected ? '' : value);
                          setPage(0);
                        }}
                      />
                    );
                  })}
                  <TextField
                    className={classes.filterControl}
                    select
                    variant="outlined"
                    size="small"
                    label="Duration"
                    value={duration}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setDuration(nextValue);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any duration</MenuItem>
                    {(filters.durations || []).map((value) => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    className={classes.filterControl}
                    select
                    variant="outlined"
                    size="small"
                    label="Audience"
                    value={audience}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setAudience(nextValue);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any audience</MenuItem>
                    {(filters.audiences || []).map((value) => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </TextField>
                  {(filters.certificateIssuers || []).length > 0 ? (
                    <TextField
                      className={`${classes.filterControl} ${classes.issuerControl}`}
                      select
                      variant="outlined"
                      size="small"
                      label="Certificate issuer"
                      value={issuer}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setIssuer(nextValue);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">Any certificate issuer</MenuItem>
                      {filters.certificateIssuers.map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </TextField>
                  ) : null}
                  {hasActiveFilters ? (
                    <Button
                      className={classes.clearFiltersBtn}
                      variant="outlined"
                      onClick={handleClearFilters}
                    >
                      Clear filters
                    </Button>
                  ) : null}
                </div>
                {resultLine ? (
                  <Typography className={classes.resultLine}>{resultLine}</Typography>
                ) : null}
                {hubTab === HUB_TAB_FREEMIUM ? (
                  <div className={classes.freemiumCallout}>
                    <Typography className={classes.freemiumCalloutText}>
                      These are the Freemium allowlist courses. Open the Learning Hub tab to request the paid catalogue, or upgrade your package.
                    </Typography>
                    <div className={classes.freemiumCalloutActions}>
                      <Button
                        variant="outlined"
                        color="primary"
                        className={classes.headerUpgradeBtn}
                        onClick={() => handleSelectHubTab(null, 1)}
                      >
                        Learning Hub
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        className={classes.headerUpgradeBtn}
                        onClick={() => setUpgradeOpen(true)}
                      >
                        Upgrade package
                      </Button>
                    </div>
                  </div>
                ) : null}
                <Grid item container spacing={2} className={classes.coursesGrid}>
                  {listLoading ? (
                    <div className={classes.gridLoading}>
                      <CircularProgress size={28} />
                    </div>
                  ) : visibleCourses.length ? visibleCourses.map((course) => (
                    <CourseItem
                      key={course.uuid || course.name}
                      course={course}
                      cta={resolveAccessCta(course, { tab: hubTab, hubGranted })}
                      onAction={handleCourseAction}
                      onDetails={setDetailsCourse}
                      actionLoadingUuid={actionLoadingUuid}
                      impersonating={impersonation.active}
                    />
                  )) : (
                    <Typography className={classes.emptyState}>No courses in this category.</Typography>
                  )}
                </Grid>
                {!listLoading && pagination.total_pages > 1 ? (
                  <div className={classes.paginationRow}>
                    <TextField
                      className={classes.pageSizeControl}
                      select
                      variant="outlined"
                      size="small"
                      label="Per page"
                      value={perpage}
                      onChange={(event) => {
                        setPerpage(normalizePageSize(event.target.value));
                        setPage(0);
                      }}
                    >
                      {LEARNING_HUB_PAGE_SIZES.map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </TextField>
                    <Pagination
                      page={pagination.pageno + 1}
                      count={pagination.total_pages}
                      onChange={(_event, value) => setPage(value - 1)}
                      color="primary"
                      shape="rounded"
                      variant="outlined"
                    />
                  </div>
                ) : null}
              </Grid>
            </Grid>
          )}
        </>
      )}

      <CourseDetailsDrawer
        course={detailsCourse}
        open={!!detailsCourse}
        onClose={() => setDetailsCourse(null)}
        actionLabel={
          detailsCourse
            ? (impersonation.active && detailsCta === 'launch'
              ? courseLaunchLabel('Open course link', false)
              : accessCtaLabel(detailsCta, impersonation.active))
            : ''
        }
        actionClassName={`${classes.courseActionBtn} ${ctaButtonClass(classes, detailsCta)}`}
        actionLoading={detailsCourse && actionLoadingUuid === detailsCourse.uuid}
        onAction={handleCourseAction}
      />
      <UpgradePackageDialog
        open={upgradeOpen}
        access={access}
        onClose={() => setUpgradeOpen(false)}
        onAlreadyApproved={() => setUpgradeBlocked(true)}
      />
    </Layout>
  );
};

export default LearningHub;
